import { useCallback, useEffect, useState } from "react";
import { services } from "../../../services";
import {
  DEFAULT_SECTION_FILTER,
  SectionFilter,
  SectionVideoStatus,
} from "../types/section-videos.types";

// simple in-memory cache
const sectionsCache: Record<string, any[]> = {};

const normalizeSections = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const payload = data as Record<string, unknown>;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.sections)) return payload.sections;
    if ("id" in payload) return [payload];
  }
  return [];
};

const useSections = (courseId?: number) => {
  const key = `sections-${courseId}`;
  const [sections, setSections] = useState<any[]>(
    normalizeSections(sectionsCache[key]),
  );
  const [loading, setLoading] = useState(false);
  const [sectionFilters, setSectionFilters] = useState<
    Record<number, SectionFilter>
  >({});

  const getSectionFilter = useCallback(
    (sectionId: number): SectionFilter =>
      sectionFilters[sectionId] ?? DEFAULT_SECTION_FILTER,
    [sectionFilters],
  );

  const fetchSections = useCallback(async () => {
    if (!courseId) return;

    try {
      setLoading(true);
      const result = await services.courses.listSections(String(courseId));
      const normalized = normalizeSections(result);
      sectionsCache[key] = normalized;
      setSections(normalized);
      return normalized;
    } catch (error) {
      console.error("Failed to fetch sections:", error);
    } finally {
      setLoading(false);
    }
  }, [courseId, key]);

  const onSectionFilterChange = useCallback(
    async (sectionId: number, partial: Partial<SectionFilter>) => {
      if (!courseId) return;

      const currentFilter = sectionFilters[sectionId] ?? DEFAULT_SECTION_FILTER;
      const nextFilter = { ...currentFilter, ...partial };

      setSectionFilters((prev) => ({
        ...prev,
        [sectionId]: nextFilter,
      }));

      const params: Record<string, string | number> = { sectionId };

      if (nextFilter.lessonStatus !== SectionVideoStatus.ALL) {
        params.lessonStatus = nextFilter.lessonStatus;
      }
      if (nextFilter.userStatus !== SectionVideoStatus.ALL) {
        params.userStatus = nextFilter.userStatus;
      }

      try {
        setLoading(true);
        const result = await services.courses.lessonsBySectionId(
          String(sectionId),
          params,
        );

        const lessons = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
          ? result.data
          : [];

        setSections((prev) => {
          const current = normalizeSections(prev);
          const next = current.map((s) =>
            String(s.id) === String(sectionId) ? { ...s, lessons } : s,
          );
          sectionsCache[key] = next;
          return next;
        });
      } catch (error) {
        console.error("Failed to filter section:", error);
      } finally {
        setLoading(false);
      }
    },
    [courseId, key, sectionFilters],
  );

  useEffect(() => {
    if (!courseId) return;

    const cached = normalizeSections(sectionsCache[key]);
    if (cached.length > 0) {
      setSections(cached);
      return;
    }

    fetchSections();
  }, [courseId, key, fetchSections]);

  return {
    sections,
    loading,
    refetch: fetchSections,
    getSectionFilter,
    onSectionFilterChange,
  };
};

export default useSections;
